import type { ReactNode } from 'react';

import { Menu } from '@actual-app/components/menu';
import type { MenuItem } from '@actual-app/components/menu';

import { HojaAcciones } from './HojaAcciones';
import type { FilaAccion } from './HojaAcciones';
import type { NombreIcono } from './Icono';

type HojaMenuProps<N extends string> = {
  isOpen: boolean;
  titulo: string;
  subtitulo?: ReactNode;
  /** Las mismas opciones que recibía el `Menu` flotante. */
  items: Array<MenuItem<N>>;
  /** Icono de cada opción; sin él, «⋯». */
  iconos?: Partial<Record<N, NombreIcono>>;
  /** Opciones en rojo (Borrar). */
  peligro?: N[];
  onMenuSelect: (name: N) => void;
  onCerrar: () => void;
  'data-testid'?: string;
};

/**
 * Menú de opciones como hoja inferior de Rumbo, en lugar de la ventanita
 * flotante heredada: mismas opciones que `Menu` (las líneas separan grupos,
 * las etiquetas se omiten), con icono y apagadas cuando no se pueden usar.
 */
export function HojaMenu<N extends string>({
  isOpen,
  titulo,
  subtitulo,
  items,
  iconos,
  peligro,
  onMenuSelect,
  onCerrar,
  'data-testid': testId,
}: HojaMenuProps<N>) {
  if (!isOpen) {
    return null;
  }
  return (
    <HojaAcciones
      titulo={titulo}
      subtitulo={subtitulo}
      onCerrar={onCerrar}
      data-testid={testId}
      filas={filasDeMenu(items, iconos, peligro, name => {
        onCerrar();
        onMenuSelect(name);
      })}
    />
  );
}

/** Pasa las opciones de un `Menu` a filas de `HojaAcciones`. */
export function filasDeMenu<N extends string>(
  items: Array<MenuItem<N>>,
  iconos: Partial<Record<N, NombreIcono>> | undefined,
  peligro: N[] | undefined,
  onSelect: (name: N) => void,
): FilaAccion[] {
  const filas: FilaAccion[] = [];
  let grupoNuevo = false;
  for (const item of items) {
    if (!item) {
      continue;
    }
    if (item === Menu.line) {
      grupoNuevo = filas.length > 0;
      continue;
    }
    if (item.type === Menu.label) {
      continue;
    }
    const name = item.name as N;
    filas.push({
      id: String(name),
      texto: item.text,
      icono: iconos?.[name] ?? 'more',
      deshabilitada: item.disabled,
      peligro: peligro?.includes(name),
      grupoNuevo,
      onPress: () => onSelect(name),
    });
    grupoNuevo = false;
  }
  return filas;
}
